import { useState, useEffect, useMemo, useCallback } from 'react';
import { dateDiffInWeeks, formatDateToUTC } from '../../../lib/helpers';
import { useNotificationContext } from '../../../providers/NotificationProvider';
import { NotificationErrors } from '../../../constants';

import {
    useActiveMigrationsQuery,
    useScheduledMigrationsQuery,
    useLazyScheduledMigrationsQuery,
    useLazySuccessMigrationsQuery,
    useLazyErrorMigrationsQuery,
    useSourcesQuery,
    useAppliancesQuery,
    useEntitlementsQuery,
    useProjectDurationQuery,
} from '../../../api';

export const useMigrationData = ({
    userOrg,
    hideMigrationsBlock,
    setHideMigrationsBlock,
    hideDrBlock,
    hideBackupBlock,
}) => {
    const { notifyError } = useNotificationContext();

    // Migrations State
    const [activeMigrations, setActiveMigrations] = useState(0);
    const [scheduledMigrations, setScheduledMigrations] = useState(0);
    const [sources, setSources] = useState(0);
    const [migrationAppliances, setMigrationAppliances] = useState(0);
    const [targetClouds, setTargetClouds] = useState(0);
    const [usedEntitlements, setUsedEntitlements] = useState(0);
    const [totalEntitlements, setTotalEntitlements] = useState(0);
    const [availableEntitlements, setAvailableEntitlements] = useState(0);
    const [projectDuration, setProjectDuration] = useState(0);

    const { today, weekFrom, monthFrom } = useMemo(() => {
        const now = new Date();
        return {
            today: formatDateToUTC(now),
            weekFrom: formatDateToUTC(new Date(now - 6 * 24 * 60 * 60 * 1000)),
            monthFrom: formatDateToUTC(new Date(now - 29 * 24 * 60 * 60 * 1000)),
        };
    }, []);

    const { data: activeMigrationsData, isLoading: isActiveLoading } = useActiveMigrationsQuery(
        { userOrg },
        { skip: userOrg === null },
    );
    const { data: scheduledMigrationsData, isLoading: isScheduledLoading } = useScheduledMigrationsQuery(
        { userOrg, fromDate: today, toDate: today },
        { skip: userOrg === null },
    );

    const [triggerSuccess, { data: successData, isLoading: isSuccessLoading, isError: isSuccessError }] = useLazySuccessMigrationsQuery();
    const [triggerError, { data: errorData, isLoading: isErrorLoading, isError: isErrorError }] = useLazyErrorMigrationsQuery();
    const [triggerScheduled, { data: scheduledData, isLoading: isScheduledPeriodLoading, isError: isScheduledError }] = useLazyScheduledMigrationsQuery();

    const periodDates = useMemo(() => ({
        day: { fromDate: today, toDate: today },
        week: { fromDate: weekFrom, toDate: today },
        month: { fromDate: monthFrom, toDate: today },
    }), [today, weekFrom, monthFrom]);

    useEffect(() => {
        if (userOrg) {
            triggerSuccess({ userOrg, ...periodDates.day });
            triggerError({ userOrg, ...periodDates.day });
            triggerScheduled({ userOrg, ...periodDates.day });
        }
    }, [userOrg, periodDates]);

    useEffect(() => {
        if (isSuccessError || isErrorError || isScheduledError) {
            notifyError({ description: NotificationErrors.migrationStatus });
        }
    }, [isSuccessError, isErrorError, isScheduledError, notifyError]);

    const fetchPeriod = useCallback((period) => {
        if (!userOrg) return;
        const dates = periodDates[period];
        triggerSuccess({ userOrg, ...dates });
        triggerError({ userOrg, ...dates });
        triggerScheduled({ userOrg, ...dates });
    }, [userOrg, periodDates, triggerSuccess, triggerError, triggerScheduled]);

    const { data: sourcesData, isLoading: isSourcesLoading } = useSourcesQuery({ userOrg }, { skip: userOrg === null });
    const { data: appliancesData, isLoading: isAppliancesLoading } = useAppliancesQuery(
        { userOrg },
        { skip: userOrg === null },
    );
    const { data: entitlementsData, isLoading: isEntitlementsLoading } = useEntitlementsQuery(
        { userOrg },
        { skip: userOrg === null },
    );
    const { data: projectDurationData, isLoading: isProjectDurationLoading } = useProjectDurationQuery(
        { userOrg },
        { skip: userOrg === null },
    );

    const dataIsLoading =
        isActiveLoading ||
        isScheduledLoading ||
        isSourcesLoading ||
        isAppliancesLoading ||
        isEntitlementsLoading ||
        isProjectDurationLoading;

    // Fetch migration data once we have the user org value
    useEffect(() => {
        if (!dataIsLoading && userOrg) {
            getAllData();
        }
    }, [userOrg, dataIsLoading]);

    const getAllData = async () => {
        await Promise.all([
            getActiveMigrations(),
            getScheduledMigrations(),
            getSources(),
            getAppliances(),
            getEntitlements(),
            getProjectDuration(),
        ]);
    };

    const migrationData = useMemo(
        () => ({
            title: 'Migrations',
            chartLabels: { completed: 'Completed Migrations', available: 'Remaining Migrations' },
            hideBlock: hideMigrationsBlock,
            used: usedEntitlements,
            available: availableEntitlements,
            total: totalEntitlements,
            stats: [
                { title: 'Migrations in Progress', link: '/migration_list', value: activeMigrations },
                { title: 'Scheduled Migrations', link: '/scheduled_migrations', value: scheduledMigrations },
                { title: 'Sources', link: '/sources', value: sources },
                { title: 'Migration Appliances', link: '/clouds', value: migrationAppliances },
                {
                    title: 'Entitlement Status',
                    link: '/projects',
                    value: `${availableEntitlements}/${totalEntitlements}`,
                },
                { title: 'Project Duration', link: '/projects', value: projectDuration },
            ],
        }),
        [
            hideMigrationsBlock,
            usedEntitlements,
            availableEntitlements,
            totalEntitlements,
            activeMigrations,
            scheduledMigrations,
            sources,
            migrationAppliances,
            projectDuration,
        ],
    );

    // Data for new Migration Dashboard cards, we can add to it as we need
    const newMigrationData = useMemo(() => {
        return {
            availableEntitlements,
            totalEntitlements,
            sources,
            migrationStatus: {
                inProgress: activeMigrationsData?.count ?? 0,
                current: {
                    scheduled: scheduledData?.count ?? 0,
                    completed: successData?.count ?? 0,
                    failed: errorData?.count ?? 0,
                    isLoading: isScheduledPeriodLoading || isSuccessLoading || isErrorLoading,
                },
                fetchPeriod,
            },
        };
    }, [availableEntitlements, totalEntitlements, sources, activeMigrationsData, scheduledData, successData, errorData, isScheduledPeriodLoading, isSuccessLoading, isErrorLoading, fetchPeriod]);

    const getActiveMigrations = () => {
        try {
            let activeMigrations = activeMigrationsData.count;
            setActiveMigrations(activeMigrations);
        } catch (error) {
            notifyError({ description: NotificationErrors.activeMigrations });
        }
    };

    const getScheduledMigrations = () => {
        try {
            let scheduledMigrations = scheduledMigrationsData.count;
            setScheduledMigrations(scheduledMigrations);
        } catch (error) {
            notifyError({ description: NotificationErrors.scheduledMigrations });
        }
    };

    const getSources = () => {
        try {
            let sources = sourcesData.count;
            setSources(sources);
        } catch (error) {
            notifyError({ description: NotificationErrors.sources });
        }
    };

    const getAppliances = () => {
        try {
            let migrationAppliances = appliancesData.content;
            setMigrationAppliances(migrationAppliances.length);

            // Return a new array of unique cloud account types
            let targetClouds = Array.from(new Set(migrationAppliances.map((app) => app.type)));
            setTargetClouds(targetClouds);
        } catch (error) {
            notifyError({ description: NotificationErrors.migrationAppliances });
        }
    };

    const getEntitlements = () => {
        try {
            let entitlementBlocks = entitlementsData?.content;

            if (entitlementBlocks && entitlementBlocks.length > 0) {
                let total = entitlementBlocks.reduce((sum, ent) => {
                    return sum + (ent.total !== -1 ? ent.total : 0);
                }, 0);

                let used = entitlementBlocks.reduce((sum, ent) => {
                    return sum + (ent.used !== -1 ? ent.used : 0);
                }, 0);

                let available = total - used;
                setUsedEntitlements(used);
                setAvailableEntitlements(available);
                setTotalEntitlements(total);
            } else {
                setUsedEntitlements(0);
                setAvailableEntitlements(0);
                setTotalEntitlements(0);
            }
        } catch (error) {
            notifyError({ description: NotificationErrors.migrationEntitlements });
        }
    };

    const getProjectDuration = () => {
        try {
            let result = projectDurationData;
            let start = result.project_start_date;
            let end = result.project_end_date;
            if (start && end) {
                let duration = dateDiffInWeeks(end, start);
                let projectDuration = duration === 1 ? `${duration} week` : `${duration} weeks`;
                setProjectDuration(projectDuration);
            } else {
                setProjectDuration('No Data');
            }
        } catch (error) {}
    };

    return { migrationData, newMigrationData };
};
